import { useCallback, useState } from 'react';

import { faTimer } from '@awesome.me/kit-f3dc60f0d0/icons/classic/regular';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Trans, useLingui } from '@lingui/react/macro';
import { Toast } from '@repo/videa-components/src/index';
import {
  ProviderSelectionDialog,
  appointmentDataAtom,
  displayHumanReadableTemplateName,
  formatTime,
  isRecordingSessionActiveAtom,
} from '@repo/videa-notes';
import { useAtomValue } from 'jotai';
import { useSelector } from 'react-redux';
import { toast } from 'sonner';

import {
  SortableTable,
  type TableColumn,
} from '@/common-components/composed/sortableTable/SortableTable';
import type { Appointment } from '@/react-query/useAppointments';
import type { ClinicalNote } from '@/react-query/videa-notes/useExistingNotes';
import { useSubmitWriteback } from '@/react-query/videa-notes/useSubmitWriteback';
import { capitalize } from '@/utils/string';
import { useFormatAppointmentDate } from '@/modules/patientList/helpers';
import { PatientInfo } from '@/modules/patientList/patientStrip/patientStripRedesigned/patientInfoSheet/PatientInfo';
import { PatientListPageLayoutContextMap } from '@/modules/patientList/types';
import type { VideaNotesAppointment } from '@/modules/videaNotes/helpers';
import { useOpenVideaNotes } from '@/modules/videaNotes/helpers';
import {
  PrecheckFailedError,
  ProviderSelectionCancelledError,
  useWritebackPrecheck,
} from '@/modules/videaNotes/hooks/useWritebackPrecheck';

import { AmbientTableActions } from './AmbientTableActions';

type SortDirection = 'asc' | 'desc';

interface AmbientTableProps {
  appointments: (VideaNotesAppointment & {
    notes: ClinicalNote[];
  })[];
  isUnifiedNotesLoading: boolean;
  isStandaloneNotesLoading: boolean;
  sortField: string;
  sortDirection: SortDirection;
  onSort: (field: string, direction: SortDirection) => void;
  invalidateNotesCache: () => void;
  scrollContainerRef?: React.Ref<HTMLDivElement>;
  sentinelRef?: React.Ref<HTMLDivElement>;
  nextAppointmentRef?: React.Ref<HTMLTableRowElement>;
  isRecentNotesView?: boolean;
}

export const AmbientTable = ({
  appointments,
  isUnifiedNotesLoading,
  isStandaloneNotesLoading,
  sortField,
  sortDirection,
  onSort,
  invalidateNotesCache,
  scrollContainerRef,
  sentinelRef,
  nextAppointmentRef,
  isRecentNotesView = false,
}: AmbientTableProps) => {
  const { t } = useLingui();
  const openVideaNotes = useOpenVideaNotes();

  const isRecordingSessionActive = useAtomValue(isRecordingSessionActiveAtom);
  const currentRecordingAppointment = useAtomValue(appointmentDataAtom);

  const user = useSelector((state: any) => state?.login?.user);

  const submitWritebackMutation = useSubmitWriteback();

  const {
    runPrecheck,
    providerDialogState,
    handleProviderConfirm,
    handleProviderCancel,
  } = useWritebackPrecheck();

  const [isPrecheckingWriteback, setIsPrecheckingWriteback] = useState(false);

  // Helper function to write back a note with precheck validation
  const writeBackNote = useCallback(
    async (note: ClinicalNote) => {
      setIsPrecheckingWriteback(true);
      try {
        const { providerPmsId } = await runPrecheck(
          note.id,
          user.primaryPracticeId,
          user.primaryOrganizationId,
        );

        submitWritebackMutation.mutate(
          { clinicalNoteId: note.id, providerPmsId },
          { onSuccess: () => invalidateNotesCache() },
        );
      } catch (err) {
        if (err instanceof ProviderSelectionCancelledError) {
          return;
        }

        let description: string;
        if (!(err instanceof PrecheckFailedError)) {
          description = t`Something went wrong. Please try again.`;
        } else if (err.reason === 'NO_PMS_PATIENT') {
          description = t`No matching patient found in your PMS. Please ensure the patient exists before saving.`;
        } else {
          description = t`No provider is available for this note. Please contact support.`;
        }

        toast(
          <Toast
            variant="error"
            title={t`Unable to save to chart`}
            description={description}
          />,
          {
            closeButton: true,
            position: 'top-right',
            style: { width: '460px' },
          },
        );
      } finally {
        setIsPrecheckingWriteback(false);
      }
    },
    [
      runPrecheck,
      user?.primaryPracticeId,
      user?.primaryOrganizationId,
      submitWritebackMutation,
      invalidateNotesCache,
      t,
    ],
  );

  // Component for formatting appointment dates
  const AppointmentDateDisplay = ({ date }: { date: string }) => {
    const formattedDate = useFormatAppointmentDate(date);
    return <div className="vai-text-sm text-gray-600">{formattedDate}</div>;
  };

  const getOperatory = (appointment: Appointment) => {
    if (!appointment.appointment.operatory?.name) {
      return '--';
    }

    return appointment.appointment.operatory.name;
  };

  const getProvider = (appointment: Appointment) => {
    if (!appointment.appointment.provider) {
      return '--';
    }

    return `${appointment.appointment.provider.firstName ?? ''} ${appointment.appointment.provider.lastName ?? ''}`.trim() ?? '--';
  };

  // One or multiple notes can be recorded for an appointment
  // We display the first one; it's always the latest from BE
  const getNotePromptName = (
    appointment: Appointment & {
      notes: ClinicalNote[];
    },
  ) => {
    if (!appointment.notes || appointment.notes.length === 0) {
      return '--';
    }

    // Get UI friendly template name for the first (most recent) note only
    const firstNote = appointment.notes[0];
    const templateName = displayHumanReadableTemplateName(firstNote);

    // Capitalize the template name from full caps
    return (
      templateName.charAt(0).toUpperCase() + templateName.slice(1).toLowerCase()
    );
  };

  const getUserDisplayName = (userData: any) => {
    if (userData?.firstName && userData?.lastName) {
      return `${capitalize(userData.firstName)} ${capitalize(
        userData.lastName,
      )}`;
    }
    if (userData?.lastName) {
      return capitalize(userData.lastName);
    }
    return 'Unknown';
  };

  const columns: TableColumn<
    VideaNotesAppointment & {
      notes: ClinicalNote[];
    }
  >[] = [
    {
      key: 'patient',
      header: <Trans>Patient Info</Trans>,
      width: 'vai-w-[282px]',
      isSortable: true,
      render: (
        appointment: Appointment & {
          notes: ClinicalNote[];
        },
        index: number,
      ) => (
        <PatientInfo
          appointment={appointment}
          index={index}
          context={PatientListPageLayoutContextMap.Ambient}
        />
      ),
    },
    {
      key: 'appointment',
      header: isRecentNotesView ? (
        <Trans>Note Created At</Trans>
      ) : (
        <Trans>Appointment Date</Trans>
      ),
      width: 'vai-w-[211px]',
      isSortable: true,
      render: (
        appointment: Appointment & {
          notes: ClinicalNote[];
        },
      ) => {
        return <AppointmentDateDisplay date={appointment.appointment.start} />;
      },
    },
    {
      key: 'provider',
      header: <Trans>Provider</Trans>,
      width: 'vai-w-[211px]',
      isSortable: true,
      render: (
        appointment: Appointment
      ) => (
        <div className="vai-text-sm text-gray-600">
          {getProvider(appointment)}
        </div>
      ),
    },
    {
      key: 'operatory',
      header: <Trans>Operatory</Trans>,
      width: 'vai-w-[211px]',
      isSortable: true,
      render: (
        appointment: Appointment
      ) => (
        <div className="vai-text-sm text-gray-600">
          {getOperatory(appointment)}
        </div>
      ),
    },
    {
      key: 'noteTemplate',
      header: <Trans>Note Template</Trans>,
      width: 'vai-w-[200px]',
      render: (
        appointment: Appointment & {
          notes: ClinicalNote[];
        },
      ) => (
        <div className="vai-text-sm text-gray-600">
          {getNotePromptName(appointment)}
        </div>
      ),
    },
    {
      key: 'recordedBy',
      header: <Trans>Recorded By</Trans>,
      width: 'vai-w-[200px]',
      render: (
        appointment: Appointment & {
          notes: ClinicalNote[];
        },
      ) => {
        if (!appointment.notes || appointment.notes.length === 0) {
          return <div className="vai-text-sm text-gray-600">--</div>;
        }

        // Only show the first (most recent) note
        const firstNote = appointment.notes[0];
        const noteUser = firstNote.user;
        const recordedByName = getUserDisplayName(noteUser);

        const duration = firstNote.duration;
        const durationText =
          duration && duration > 0 ? formatTime(duration) : '';

        return (
          <div className="vai-flex vai-flex-col vai-gap-1">
            <div className="vai-text-sm text-gray-600">{recordedByName}</div>
            {durationText && (
              <div className="vai-flex vai-items-center vai-gap-1">
                <FontAwesomeIcon icon={faTimer} size="sm" />
                <span className="vai-text-sm">{durationText}</span>
              </div>
            )}
          </div>
        );
      },
    },
    {
      key: 'actions',
      header: <Trans>Note Actions</Trans>,
      width: 'vai-min-w-[200px]',
      render: (
        appointment: Appointment & {
          notes: ClinicalNote[];
          isStandalone?: boolean;
        },
      ) => {
        const isNotesLoading = appointment.isStandalone
          ? isStandaloneNotesLoading
          : isUnifiedNotesLoading;
        return (
          <AmbientTableActions
            user={user}
            appointment={appointment}
            notes={appointment.notes}
            isNotesLoading={isNotesLoading}
            isRecordingSessionActive={isRecordingSessionActive}
            currentRecordingAppointment={currentRecordingAppointment}
            isWritebackLoading={
              isPrecheckingWriteback || submitWritebackMutation.isLoading
            }
            openVideaNotes={openVideaNotes}
            writeBackNote={writeBackNote}
          />
        );
      },
    },
  ];

  return (
    <>
      <SortableTable
        data={appointments}
        columns={columns}
        sortField={sortField}
        sortDirection={sortDirection}
        onSort={onSort}
        rowKey={(appointment, index) =>
          `${
            appointment.appointment.unifiedId ||
            (appointment as any).appointment.standalonePatientVisitId ||
            'unknown'
          }-${index}`
        }
        className="vai-min-h-0 vai-flex-1"
        scrollRef={scrollContainerRef}
        sentinelRef={sentinelRef}
        nextAppointmentRef={nextAppointmentRef}
      />
      <ProviderSelectionDialog
        open={providerDialogState.open}
        providers={providerDialogState.providers}
        onConfirm={handleProviderConfirm}
        onCancel={handleProviderCancel}
      />
    </>
  );
};
